import { useCallback, useEffect, useState } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "kopateli.theme";

const readStored = (): Theme | null => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "dark" || value === "light" ? value : null;
  } catch {
    return null; // private mode / blocked storage: fall back to the system preference
  }
};

const systemTheme = (): Theme =>
  typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";

/**
 * Theme with a remembered choice. Until the user picks one, `data-theme` is absent and
 * the CSS follows `prefers-color-scheme`; once picked, the attribute wins in both
 * directions (визуальный контракт: "уважать prefers-color-scheme, выбор запоминать").
 */
export const useTheme = (): {
  theme: Theme;
  explicit: Theme | null;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
} => {
  const [explicit, setExplicit] = useState<Theme | null>(readStored);
  const [system, setSystem] = useState<Theme>(systemTheme);

  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const query = matchMedia("(prefers-color-scheme: light)");
    const onChange = (): void => setSystem(query.matches ? "light" : "dark");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (explicit) root.dataset.theme = explicit;
    else delete root.dataset.theme;
  }, [explicit]);

  const setTheme = useCallback((next: Theme) => {
    setExplicit(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not fatal: the theme still applies for this session.
    }
  }, []);

  const theme = explicit ?? system;
  const toggle = useCallback(
    () => setTheme(theme === "dark" ? "light" : "dark"),
    [setTheme, theme],
  );

  return { theme, explicit, setTheme, toggle };
};

export const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState<boolean>(
    () =>
      typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const query = matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (): void => setReduced(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return reduced;
};
